import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Cpu, HardDrive, Database, Fan, Zap, Box, Monitor, X,
    ShoppingCart, RotateCcw, LogIn, Search, AlertTriangle,
    CheckCircle, SlidersHorizontal, XCircle,
    ChevronLeft, ChevronRight, ChevronDown
} from 'lucide-react';
import { pcBuilderService } from '../../services/pcBuilderService';
import { cartService } from '../../services/cartService';
import catalogService from '../../services/catalogService';
import Header from '../../components/common/Header';
import Footer from '../../components/common/Footer';
import { getImageUrl, generateSlug } from '../../utils/helpers';
import { toast } from 'react-toastify';
import { useCart } from '../../contexts/CartContext';

// ==========================================
// CONSTANT TRẠNG THÁI
// ==========================================
const PRODUCT_STATUS = {
    HIDDEN: 0,
    COMING_SOON: 1,
    IN_STOCK: 2,
    PRE_ORDER: 3,
    OUT_OF_STOCK: 4,
    DISCONTINUED: 5
};

// ==========================================
// HELPERS
// ==========================================
const stripHtml = (html) => {
    if (!html) return '';
    return html
        .replace(/<\/(p|div|li|h[1-6])>/gi, ' ') 
        .replace(/<br\s*\/?>/gi, ' ') 
        .replace(/<[^>]*>?/gm, '') 
        .replace(/&nbsp;/g, ' ') 
        .replace(/\s+/g, ' ') 
        .trim();
};

const getActivePrice = (p) => {
    if (!p) return 0;
    const isFlashSale = p.itemFlashSalePrice && p.itemFlashSalePrice > 0 && p.itemFlashSaleQuantity > 0;
    return isFlashSale ? p.itemFlashSalePrice : (p.itemSalePrice || p.itemReleasePrice || p.itemPrice || 0);
};

const COMPONENT_ICONS = {
    'CPU':    <Cpu size={22} />,
    'MAIN':   <Database size={22} />,
    'RAM':    <Database size={22} />,
    'VGA':    <Monitor size={22} />,
    'SSD':    <HardDrive size={22} />,
    'HDD':    <HardDrive size={22} />,
    'PSU':    <Zap size={22} />,
    'CASE':   <Box size={22} />,
    'COOLER': <Fan size={22} />,
};

// 👉 TỐI ƯU 1: Gán Width/Height cụ thể hoặc Aspect Ratio cho Icon để tránh CLS
const SlotIcon = React.memo(({ comp }) => {
    const [imgError, setImgError] = useState(false);
    const hasIcon = comp.icon && typeof comp.icon === 'string' && comp.icon.trim() !== '';

    if (hasIcon && !imgError) {
        const finalUrl = getImageUrl(comp.icon);
        return (
            <img 
                src={finalUrl} 
                alt={comp.componentName} 
                width={32}
                height={32}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-contain drop-shadow-sm aspect-square"
                onError={() => setImgError(true)} 
            />
        );
    }
    return COMPONENT_ICONS[comp.componentCode] || <Box size={22} />;
});

// Dropdown Memo (Giữ nguyên)
const SingleSelectDropdown = React.memo(({ label, options, value, onChange, isOpen, onToggle }) => {
    const selectedLabel = options.find(o => o.value === value)?.label || "Tất cả";
    return (
        <div className="flex flex-col gap-1 relative">
            <label className="text-[10px] font-bold text-gray-500 uppercase truncate" title={label}>{label}</label>
            <button
                onClick={onToggle}
                className={`flex items-center justify-between text-sm border rounded-lg px-2 py-1.5 outline-none shadow-sm transition-all bg-white
                    ${value && value !== 'all' && value !== '' ? 'border-tnc-orange text-tnc-orange font-bold' : 'border-gray-200 text-gray-700 font-medium hover:border-tnc-orange'}`}
            >
                <span className="truncate max-w-[120px]">{selectedLabel}</span>
                <ChevronDown size={14} className={`transition-transform shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && (
                <>
                    <div className="fixed inset-0 z-40" onClick={onToggle}></div>
                    <div className="absolute top-full left-0 mt-1 w-full min-w-[200px] bg-white border border-orange-200 rounded-xl shadow-xl z-[70] p-2 flex flex-col gap-1">
                        <div className="max-h-48 overflow-y-auto custom-scrollbar">
                            {options.map(opt => (
                                <button
                                    key={opt.value}
                                    onClick={() => { onChange(opt.value); onToggle(); }}
                                    className={`w-full text-left text-xs px-3 py-2 rounded-lg transition-colors ${value === opt.value ? 'bg-orange-50 text-tnc-orange font-bold' : 'text-gray-700 hover:bg-gray-50 font-medium'}`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
});

// =============================================
// LOGIC TƯƠNG THÍCH (Giữ nguyên)
// =============================================
function checkConflict(itemA, itemB) {
    const groupsA = itemA?.compatibilityGroups || [];
    const groupsB = itemB?.compatibilityGroups || [];
    if (groupsA.length === 0 || groupsB.length === 0) return null;

    const conflicts = [];
    for (const ga of groupsA) {
        const matchingB = groupsB.filter(gb => gb.groupType === ga.groupType);
        if (matchingB.length > 0 && !matchingB.some(gb => gb.groupValue === ga.groupValue)) {
            conflicts.push({
                groupType: ga.groupType,
                valueA: ga.groupValue,
                valueB: matchingB[0].groupValue,
            });
        }
    }
    return conflicts.length > 0 ? conflicts : null;
}

function getItemCompatStatus(item, selectedParts, skipCode) {
    const groups = item?.compatibilityGroups || [];
    if (groups.length === 0) return 'unknown';

    let hasAnyCheck = false;
    for (const [code, selectedItem] of Object.entries(selectedParts)) {
        if (code === skipCode) continue;
        const selectedGroups = selectedItem?.compatibilityGroups || [];
        if (selectedGroups.length === 0) continue;

        hasAnyCheck = true;
        for (const ga of groups) {
            const matchingB = selectedGroups.filter(gb => gb.groupType === ga.groupType);
            if (matchingB.length > 0 && !matchingB.some(gb => gb.groupValue === ga.groupValue)) {
                return 'incompatible';
            }
        }
        for (const gb of selectedGroups) {
            const matchingA = groups.filter(ga => ga.groupType === gb.groupType);
            if (matchingA.length > 0 && !matchingA.some(ga => ga.groupValue === gb.groupValue)) {
                return 'incompatible';
            }
        }
    }
    return hasAnyCheck ? 'compatible' : 'unknown';
}

function getAllConflicts(selectedParts, components) {
    const warnings = [];
    const codes = Object.keys(selectedParts);
    for (let i = 0; i < codes.length; i++) {
        for (let j = i + 1; j < codes.length; j++) {
            const codeA = codes[i];
            const codeB = codes[j];
            const conflicts = checkConflict(selectedParts[codeA], selectedParts[codeB]);
            if (conflicts) {
                const nameA = components.find(c => c.componentCode === codeA)?.componentName || codeA;
                const nameB = components.find(c => c.componentCode === codeB)?.componentName || codeB;
                conflicts.forEach(c => {
                    warnings.push({
                        codeA, codeB, nameA, nameB,
                        groupType: c.groupType,
                        valueA: c.valueA,
                        valueB: c.valueB,
                    });
                });
            }
        }
    }
    return warnings;
}

// =============================================
// COMPONENT CHÍNH
// =============================================

const BuildPCPage = () => {
    const navigate = useNavigate();
    const { fetchCart } = useCart();

    const [components, setComponents] = useState([]);
    const [selectedParts, setSelectedParts] = useState(() => {
        try {
            const saved = localStorage.getItem('pc_builder_draft');
            return saved ? JSON.parse(saved) : {};
        } catch { return {}; }
    });
    const [loading, setLoading] = useState(true);

    const itemsCache = useRef({});

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeComponent, setActiveComponent] = useState(null);
    const [allItems, setAllItems] = useState([]);
    const [loadingItems, setLoadingItems] = useState(false);

    // Search & filter state
    const [searchInput, setSearchInput] = useState(''); 
    const [searchQuery, setSearchQuery] = useState(''); 
    const [filterBrand, setFilterBrand] = useState('');
    const [filterCompat, setFilterCompat] = useState('all');
    const [filterStock, setFilterStock] = useState('all');
    const [filterPriceMax, setFilterPriceMax] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [specFilters, setSpecFilters] = useState({});

    const [adminSpecs, setAdminSpecs] = useState([]);
    const [selectedTechSpecs, setSelectedTechSpecs] = useState({});
    const [openDropdown, setOpenDropdown] = useState(null);

    // Phân trang
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 12;

    const isAuthenticated = !!localStorage.getItem('access_token');

    // Cuộn lên top nhanh để không nhảy layout
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchQuery(searchInput);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        document.title = "Xây dựng cấu hình PC | Thành Nam PC";
        let metaDescriptionTag = document.querySelector('meta[name="description"]');
        if (metaDescriptionTag) {
            const description = "Xây dựng cấu hình PC chính hãng tại Thành Nam PC. Đặt hàng online đơn giản, giao hàng tận nhà nhanh chóng tại Quảng Ninh. Cấu hình đa dạng, giá tốt cùng dịch vụ sau bán hàng uy tín, chuyên nghiệp.";
            metaDescriptionTag.setAttribute("content", description);
        }

        const fetchData = async () => {
            try {
                const [compRes, specRes] = await Promise.all([
                    pcBuilderService.getComponents(),
                    catalogService.getSpecs().catch(() => []) 
                ]);
                
                setComponents(Array.isArray(compRes?.data) ? compRes.data : (Array.isArray(compRes) ? compRes : []));
                
                const specsData = Array.isArray(specRes?.data) ? specRes.data : (Array.isArray(specRes) ? specRes : []);
                setAdminSpecs(specsData);
            } catch {
                toast.error("Không thể tải dữ liệu hệ thống.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        localStorage.setItem('pc_builder_draft', JSON.stringify(selectedParts));
    }, [selectedParts]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, filterBrand, filterCompat, filterStock, filterPriceMax, specFilters, selectedTechSpecs, activeComponent]);

    const conflicts = useMemo(() => getAllConflicts(selectedParts, components), [selectedParts, components]);
    const conflictCodes = useMemo(() => {
        const s = new Set();
        conflicts.forEach(w => { s.add(w.codeA); s.add(w.codeB); });
        return s;
    }, [conflicts]);

    const availableBrands = useMemo(() => {
        const brands = new Map();
        allItems.forEach(item => {
            if (item.brand?.brandId) brands.set(item.brand.brandId, item.brand.brandName);
        });
        return Array.from(brands.entries()).map(([id, name]) => ({ id, name }));
    }, [allItems]);

    const availableSpecs = useMemo(() => {
        const specs = {};
        allItems.forEach(item => {
            if (item.compatibilityGroups) {
                item.compatibilityGroups.forEach(g => {
                    if (!specs[g.groupType]) specs[g.groupType] = new Set();
                    specs[g.groupType].add(g.groupValue);
                });
            }
        });
        return Object.keys(specs).map(key => ({
            groupType: key,
            values: Array.from(specs[key]).sort()
        }));
    }, [allItems]);

    const availableTechSpecs = useMemo(() => {
        if (!allItems || allItems.length === 0) return [];
        
        const activeValuesMap = {};
        const filterableSpecs = adminSpecs?.filter(s => s.isFilterable)?.map(s => s.specName?.trim().toLowerCase()) || [];

        allItems.forEach(p => {
            if (p._parsedTechSpecs && Array.isArray(p._parsedTechSpecs)) {
                p._parsedTechSpecs.forEach(spec => {
                    const specName = spec.name;
                    if (specName && (filterableSpecs.length === 0 || filterableSpecs.includes(specName.toLowerCase()))) {
                        if (!activeValuesMap[specName]) {
                            activeValuesMap[specName] = new Set();
                        }
                        if (spec.value) {
                            activeValuesMap[specName].add(spec.value);
                        }
                    }
                });
            }
        });

        const formattedSpecs = [];
        Object.keys(activeValuesMap).forEach(specName => {
            formattedSpecs.push({
                name: specName,
                values: Array.from(activeValuesMap[specName]).sort()
            });
        });

        return formattedSpecs;
    }, [allItems, adminSpecs]);

    const handleTechSpecChange = (specName, specValue) => {
        setSelectedTechSpecs(prev => {
            const currentValues = prev[specName] || [];
            if (currentValues.includes(specValue)) {
                const newValues = currentValues.filter(v => v !== specValue);
                if (newValues.length === 0) {
                    const newState = { ...prev };
                    delete newState[specName];
                    return newState;
                }
                return { ...prev, [specName]: newValues };
            } else {
                return { ...prev, [specName]: [...currentValues, specValue] };
            }
        });
    };

    const processedItems = useMemo(() => {
        if (!activeComponent) return [];

        let items = [...allItems];

        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            items = items.filter(i => i.itemName?.toLowerCase().includes(q) || i.itemCode?.toLowerCase().includes(q));
        }

        items = items.map(item => ({
            ...item,
            _compatStatus: getItemCompatStatus(item, selectedParts, activeComponent.componentCode),
        }));

        if (filterCompat === 'compatible') {
            items = items.filter(i => i._compatStatus !== 'incompatible');
        } else if (filterCompat === 'incompatible') {
            items = items.filter(i => i._compatStatus === 'incompatible');
        }

        if (filterBrand) {
            items = items.filter(i => String(i.brand?.brandId) === filterBrand);
        }

        if (filterStock === String(PRODUCT_STATUS.IN_STOCK)) {
            items = items.filter(i => (i.itemStatus ?? i.status) === PRODUCT_STATUS.IN_STOCK);
        } else if (filterStock === String(PRODUCT_STATUS.PRE_ORDER)) {
            items = items.filter(i => (i.itemStatus ?? i.status) === PRODUCT_STATUS.PRE_ORDER);
        }

        if (filterPriceMax && !isNaN(Number(filterPriceMax))) {
            items = items.filter(i => getActivePrice(i) <= Number(filterPriceMax));
        }

        Object.entries(specFilters).forEach(([type, value]) => {
            if (value) {
                items = items.filter(i => 
                    i.compatibilityGroups?.some(g => g.groupType === type && g.groupValue === value)
                );
            }
        });

        Object.entries(selectedTechSpecs).forEach(([specName, allowedValues]) => {
            if (allowedValues && allowedValues.length > 0) {
                items = items.filter(i => {
                    if (!i._parsedTechSpecs) return false;
                    const productSpec = i._parsedTechSpecs.find(s => s.name === specName.trim());
                    return productSpec && allowedValues.includes(productSpec.value);
                });
            }
        });

        const order = { compatible: 0, unknown: 1, incompatible: 2 };
        items.sort((a, b) => {
            const diff = (order[a._compatStatus] ?? 1) - (order[b._compatStatus] ?? 1);
            if (diff !== 0) return diff;
            return getActivePrice(a) - getActivePrice(b);
        });

        return items;
    }, [allItems, activeComponent, selectedParts, searchQuery, filterBrand, filterCompat, filterStock, filterPriceMax, specFilters, selectedTechSpecs]);

    const totalPages = Math.ceil(processedItems.length / itemsPerPage) || 1;
    const paginatedItems = processedItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    const getProductImage = (item) => {
        let path = item.itemImage;
        if (item.images && item.images.length > 0) {
            const mainImg = item.images.find(i => i.isMain);
            path = mainImg ? mainImg.itemImageUrl : item.images[0].itemImageUrl;
        }
        return getImageUrl(path);
    };

    const formatPrice = (price) =>
        new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(price || 0);

    const handleOpenSelection = async (comp) => {
        setActiveComponent(comp);
        setIsModalOpen(true);
        setLoadingItems(true);
        setAllItems([]);
        
        setSearchInput('');
        setSearchQuery('');
        setFilterBrand('');
        setFilterCompat('all');
        setFilterStock('all'); 
        setFilterPriceMax('');
        setSpecFilters({});
        setSelectedTechSpecs({}); 
        setOpenDropdown(null);    
        setShowFilters(false);
        setCurrentPage(1);

        if (itemsCache.current[comp.componentCode]) {
            setAllItems(itemsCache.current[comp.componentCode]);
            setLoadingItems(false);
            return;
        }

        try {
            const res = await pcBuilderService.getItemsForComponent(comp.componentCode);
            const raw = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);  

            const filteredItems = raw.filter(item => {
                const price = getActivePrice(item);
                const status = item.itemStatus ?? item.status;
                const isStatusValid = ![
                    PRODUCT_STATUS.HIDDEN, 
                    PRODUCT_STATUS.OUT_OF_STOCK, 
                    PRODUCT_STATUS.DISCONTINUED
                ].includes(status);
                return price > 0 && isStatusValid;
            });

            const processedInitData = filteredItems.map(item => {
                let parsedTechSpecs = [];
                let specsRaw = item.itemSpecs;
                if (typeof specsRaw === 'string') {
                    try { specsRaw = JSON.parse(specsRaw); } catch { specsRaw = []; }
                }
                if (Array.isArray(specsRaw)) {
                    parsedTechSpecs = specsRaw.map(spec => ({
                        name: spec.spec?.specName?.trim(),
                        value: stripHtml(spec.specValue)
                    })).filter(s => s.name && s.value);
                }

                return {
                    ...item,
                    _parsedTechSpecs: parsedTechSpecs 
                };
            });

            itemsCache.current[comp.componentCode] = processedInitData;
            setAllItems(processedInitData);

        } catch {
            toast.error("Lỗi khi tải danh sách sản phẩm.");
        } finally {
            setLoadingItems(false);
        }
    };

    const handleSelectPart = (item) => {
        setSelectedParts(prev => ({ ...prev, [activeComponent.componentCode]: item }));
        setIsModalOpen(false);
    };

    const handleRemovePart = (code) => {
        setSelectedParts(prev => { const s = { ...prev }; delete s[code]; return s; });
    };

    const handleResetBuilder = () => {
        setSelectedParts({});
        localStorage.removeItem('pc_builder_draft');
    };

    const handleSpecFilterChange = (groupType, value) => {
        setSpecFilters(prev => ({
            ...prev,
            [groupType]: value
        }));
    };

    const totalPrice = Object.values(selectedParts).reduce((sum, item) => sum + getActivePrice(item), 0);
    const totalItems = Object.values(selectedParts).length;

    const handleAddAllToCart = async () => {
        if (totalItems === 0) return toast.warning("Vui lòng chọn ít nhất 1 linh kiện!");
        try {
            await Promise.all(Object.values(selectedParts).map(item => cartService.addToCart(item.itemId, 1)));
            fetchCart();
            localStorage.removeItem('pc_builder_draft');
            setSelectedParts({});
            toast.success("Đã thêm toàn bộ cấu hình vào giỏ hàng!");
            navigate('/cart');
        } catch (error) {
            toast.error("Lỗi khi thêm vào giỏ hàng. Vui lòng đăng nhập!");
            if (error.response?.status === 401 || error.response?.status === 403) navigate('/login');
        }
    };

    const activeFiltersCount = [
        filterBrand, 
        filterCompat !== 'all', 
        filterStock !== 'all', 
        filterPriceMax, 
        ...Object.values(specFilters).filter(Boolean),
        ...Object.values(selectedTechSpecs).filter(arr => arr.length > 0)
    ].filter(Boolean).length;

    const compatCount   = processedItems.filter(i => i._compatStatus === 'compatible').length;
    const incompatCount = processedItems.filter(i => i._compatStatus === 'incompatible').length;
    const unknownCount  = processedItems.filter(i => i._compatStatus === 'unknown').length;

    return (
        // 👉 TỐI ƯU 2: Đặt min-h-screen để chống Footer nảy lên xuống khi tải trang
        <div className="flex flex-col min-h-screen bg-gray-50">
            <Header />

            <main className="flex-grow container mx-auto px-4 py-8 min-h-[70vh]">
                <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-extrabold text-[#002855] uppercase tracking-wide">
                            Xây dựng cấu hình PC
                        </h1>
                        <p className="text-gray-500 mt-1">Tự do lựa chọn linh kiện, kiểm tra tương thích tự động.</p>
                    </div>
                    <button
                        onClick={handleResetBuilder}
                        className="flex items-center gap-2 text-sm font-bold text-red-500 hover:bg-red-50 px-4 py-2 rounded-lg transition-colors border border-red-200 bg-white"
                    >
                        <RotateCcw size={16} /> Làm mới cấu hình
                    </button>
                </div>

                {conflicts.length > 0 && (
                    <div className="mb-5 bg-amber-50 border border-amber-300 rounded-xl p-4 flex flex-col gap-2 shadow-sm animate-in fade-in slide-in-from-top-2">
                        <div className="flex items-center gap-2 font-bold text-amber-700 text-sm">
                            <AlertTriangle size={18} className="shrink-0" />
                            Phát hiện {conflicts.length} vấn đề tương thích — bạn vẫn có thể tiếp tục, nhưng hãy kiểm tra lại:
                        </div>
                        <ul className="flex flex-col gap-1 pl-6">
                            {conflicts.map((w, idx) => (
                                <li key={idx} className="text-xs text-amber-700 list-disc">
                                    <strong>{w.nameA}</strong> ({w.valueA}) và <strong>{w.nameB}</strong> ({w.valueB}) không tương thích về <strong>{w.groupType}</strong>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="flex flex-col lg:flex-row gap-8 relative">
                    <div className="w-full lg:w-2/3 space-y-3">
                        {/* 👉 TỐI ƯU 3: Thay thế vòng Spinner xoay xoay bằng Skeleton UI */}
                        {loading ? (
                            <div className="space-y-3 animate-pulse">
                                {[...Array(8)].map((_, i) => (
                                    <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 h-[90px] flex items-center gap-4">
                                        <div className="w-10 h-10 bg-gray-200 rounded-lg shrink-0"></div>
                                        <div className="h-5 bg-gray-200 rounded w-24"></div>
                                        <div className="ml-auto w-[120px] h-10 bg-gray-200 rounded-lg shrink-0"></div>
                                    </div>
                                ))}
                            </div>
                        ) : components.map(comp => {
                            const selectedItem = selectedParts[comp.componentCode];
                            const hasConflict  = conflictCodes.has(comp.componentCode);

                            return (
                                <div
                                    key={comp.componentId}
                                    className={`bg-gradient-to-r from-orange-100 to-white rounded-xl shadow-sm border p-4 transition-all hover:shadow-md
                                        ${hasConflict
                                            ? 'border-amber-400 bg-amber-50/30'
                                            : 'border-orange-200/60 hover:border-tnc-orange'}`}
                                >
                                    <div className="flex flex-col md:flex-row gap-4 items-center">
                                        <div className="w-full md:w-48 flex items-center gap-3 shrink-0">
                                            <div className={`p-2 rounded-lg shadow-sm flex items-center justify-center w-10 h-10 shrink-0 ${hasConflict ? 'bg-amber-100 text-amber-600' : 'bg-white text-[#002855]'}`}>
                                                <SlotIcon comp={comp} />
                                            </div>
                                            <div>
                                                <p className="font-bold text-gray-800 text-sm">{comp.componentName}</p>
                                                {selectedItem && !hasConflict && (
                                                    <span className="text-[11px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded flex items-center gap-1 w-fit mt-1">
                                                        <CheckCircle size={10}/> Đã chọn
                                                    </span>
                                                )}
                                                {hasConflict && (
                                                    <span className="text-[11px] font-bold text-amber-600 bg-amber-100 px-2 py-0.5 rounded flex items-center gap-1 w-fit mt-1">
                                                        <AlertTriangle size={10}/> Xung đột
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex-1 w-full border-t md:border-t-0 md:border-l border-orange-100 pt-4 md:pt-0 md:pl-4">
                                            {selectedItem ? (
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="flex items-center gap-3">
                                                        {/* 👉 TỐI ƯU 4: Thêm Lazy Load và size cứng cho ảnh nhỏ */}
                                                        <img
                                                            src={getProductImage(selectedItem)}
                                                            alt={selectedItem.itemName}
                                                            loading="lazy"
                                                            decoding="async"
                                                            width={56} height={56}
                                                            className={`w-14 h-14 object-contain aspect-square bg-white rounded border p-1 shrink-0 ${hasConflict ? 'border-amber-300' : 'border-orange-100'}`}
                                                        />
                                                        <div>
                                                            <Link
                                                                to={`/san-pham/${generateSlug(selectedItem.itemName || 'san-pham')}-id${selectedItem.itemId}`}
                                                                target="_blank"
                                                                className="font-bold text-gray-800 hover:text-tnc-orange line-clamp-2 text-sm leading-snug"
                                                            >
                                                                {selectedItem.itemName}
                                                            </Link>
                                                            <div className="font-extrabold text-tnc-orange mt-1 text-sm">{formatPrice(getActivePrice(selectedItem))}</div>
                                                            {selectedItem.compatibilityGroups?.length > 0 && (
                                                                <div className="flex flex-wrap gap-1 mt-1">
                                                                    {selectedItem.compatibilityGroups.map((g, i) => (
                                                                        <span key={i} className="text-[10px] bg-blue-50 text-blue-600 border border-blue-100 px-1.5 py-0.5 rounded font-bold">
                                                                            {g.groupType}: {g.groupValue}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col gap-2 shrink-0">
                                                        <button
                                                            onClick={() => handleOpenSelection(comp)}
                                                            className="text-xs font-bold text-tnc-orange hover:underline bg-orange-100 px-3 py-1.5 rounded"
                                                        >
                                                            Sửa
                                                        </button>
                                                        <button
                                                            onClick={() => handleRemovePart(comp.componentCode)}
                                                            className="text-xs font-bold text-red-500 hover:underline bg-red-50 px-3 py-1.5 rounded"
                                                        >
                                                            Xóa
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex items-center justify-between h-full min-h-[56px]">
                                                    <span className="text-sm text-gray-400 italic">Chưa chọn linh kiện...</span>
                                                    <button
                                                        onClick={() => handleOpenSelection(comp)}
                                                        className="bg-tnc-orange text-white text-sm font-bold px-5 py-2 rounded-lg hover:bg-tnc-orange-hover transition-colors shadow-sm whitespace-nowrap"
                                                    >
                                                        + Chọn {comp.componentCode}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="w-full lg:w-1/3">
                        <div className="bg-gradient-to-b from-orange-100 to-white rounded-2xl shadow-lg border border-orange-200/60 p-6 sticky top-28">
                            <h3 className="text-xl font-bold text-[#002855] mb-4 border-b border-orange-200/60 pb-3">
                                Chi phí dự tính
                            </h3>

                            {conflicts.length > 0 && (
                                <div className="mb-4 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                    <AlertTriangle size={14} className="shrink-0" />
                                    Có {conflicts.length} cảnh báo tương thích
                                </div>
                            )}

                            <div className="space-y-2.5 mb-6 max-h-[40vh] overflow-y-auto pr-1 custom-scrollbar">
                                {Object.entries(selectedParts).map(([code, item]) => {
                                    const comp = components.find(c => c.componentCode === code);
                                    const hasConflict = conflictCodes.has(code);
                                    return (
                                        <div key={code} className={`flex justify-between items-start gap-2 text-sm p-2 rounded-lg ${hasConflict ? 'bg-amber-50 border border-amber-200' : 'bg-white/60'}`}>
                                            <div className="flex items-start gap-2 flex-1 min-w-0">
                                                {hasConflict && <AlertTriangle size={13} className="text-amber-500 shrink-0 mt-0.5" />}
                                                <div className="min-w-0">
                                                    <span className="text-[10px] text-gray-400 font-bold uppercase">{comp?.componentName || code}</span>
                                                    <p className="text-gray-700 line-clamp-1 font-medium leading-snug">{item.itemName}</p>
                                                </div>
                                            </div>
                                            <span className="font-bold text-gray-800 shrink-0 text-xs">{formatPrice(getActivePrice(item))}</span>
                                        </div>
                                    );
                                })}
                                {totalItems === 0 && (
                                    <p className="text-sm text-gray-400 italic text-center py-4">Chưa có linh kiện nào.</p>
                                )}
                            </div>

                            <div className="border-t border-orange-200/60 pt-4 mb-5">
                                <div className="flex justify-between items-center">
                                    <span className="font-bold text-gray-600 text-sm">Tổng ({totalItems} món):</span>
                                    <span className="font-black text-2xl text-tnc-orange">{formatPrice(totalPrice)}</span>
                                </div>
                            </div>

                            {isAuthenticated ? (
                                <button
                                    onClick={handleAddAllToCart}
                                    disabled={totalItems === 0}
                                    className="w-full bg-gradient-to-r from-tnc-orange to-yellow-500 text-white font-extrabold text-base py-4 rounded-xl flex items-center justify-center gap-2 hover:from-tnc-orange-hover hover:to-yellow-600 transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ShoppingCart size={20} /> THÊM TẤT CẢ VÀO GIỎ
                                </button>
                            ) : (
                                <div className="flex flex-col gap-2">
                                    <p className="text-xs text-red-500 font-bold text-center bg-red-50 p-2 rounded-lg border border-red-100">
                                        Vui lòng đăng nhập để thêm vào giỏ hàng!
                                    </p>
                                    <button
                                        onClick={() => navigate('/login', { state: { from: '/build-pc' } })}
                                        className="w-full bg-tnc-orange text-white font-extrabold py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-tnc-orange-hover transition-all shadow-md"
                                    >
                                        <LogIn size={20} /> ĐĂNG NHẬP NGAY
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>

            <Footer />

            {/* ===================== MODAL CHỌN SẢN PHẨM ===================== */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden">

                        {/* Header modal */}
                        <div className="bg-[#002855] p-4 text-white flex justify-between items-center shrink-0">
                            <div>
                                <h2 className="text-lg font-bold uppercase">Chọn {activeComponent?.componentName}</h2>
                                {!loadingItems && (
                                    <p className="text-xs text-blue-200 mt-0.5">
                                        {processedItems.length} sản phẩm
                                        {compatCount > 0 && <span className="text-green-300 ml-2">· {compatCount} tương thích</span>}
                                        {incompatCount > 0 && <span className="text-amber-300 ml-2">· {incompatCount} có vấn đề</span>}
                                        {unknownCount > 0 && <span className="text-gray-300 ml-2">· {unknownCount} chưa xác định</span>}
                                    </p>
                                )}
                            </div>
                            <button onClick={() => setIsModalOpen(false)} className="hover:bg-white/20 p-2 rounded-full transition-colors">
                                <X size={22} />
                            </button>
                        </div>

                        {/* Search + Filter bar */}
                        <div className="border-b border-gray-200 bg-gray-50 px-4 py-3 shrink-0 flex flex-col gap-3 shadow-sm relative z-[60]">
                            <div className="flex gap-2 items-center">
                                {/* Search */}
                                <div className="flex-1 relative">
                                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        value={searchInput}
                                        onChange={e => setSearchInput(e.target.value)}
                                        placeholder={`Tìm kiếm ${activeComponent?.componentName}...`}
                                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange bg-white transition-shadow"
                                        autoFocus
                                    />
                                    {searchInput && (
                                        <button onClick={() => { setSearchInput(''); setSearchQuery(''); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                            <XCircle size={15} />
                                        </button>
                                    )}
                                </div>

                                {/* Toggle filter */}
                                <button
                                    onClick={() => setShowFilters(v => !v)}
                                    className={`flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-lg border transition-all shadow-sm ${showFilters ? 'bg-tnc-orange text-white border-tnc-orange' : 'bg-white border-gray-200 text-gray-700 hover:border-tnc-orange hover:text-tnc-orange'}`}
                                >
                                    <SlidersHorizontal size={15} /> Lọc
                                    {activeFiltersCount > 0 && (
                                        <span className={`rounded-full text-[10px] font-black w-4 h-4 flex items-center justify-center ${showFilters ? 'bg-white text-tnc-orange' : 'bg-tnc-orange text-white'}`}>
                                            {activeFiltersCount}
                                        </span>
                                    )}
                                </button>
                            </div>

                            {/* Filter panel */}
                            {showFilters && (
                                <div className="pt-3 border-t border-gray-200 mt-1 flex flex-col gap-4">
                                    
                                    {/* --- PHẦN 1: LỌC CƠ BẢN --- */}
                                    <div>
                                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                                            <SingleSelectDropdown
                                                label="Tương thích"
                                                options={[
                                                    { value: 'all', label: 'Tất cả sản phẩm' },
                                                    { value: 'compatible', label: 'Chỉ lọc các sản phẩm tương thích ✅' }
                                                ]}
                                                value={filterCompat}
                                                onChange={setFilterCompat}
                                                isOpen={openDropdown === 'compat'}
                                                onToggle={() => setOpenDropdown(openDropdown === 'compat' ? null : 'compat')}
                                            />

                                            <SingleSelectDropdown
                                                label="Tình trạng"
                                                options={[
                                                    { value: 'all', label: 'Tất cả tình trạng' },
                                                    { value: String(PRODUCT_STATUS.IN_STOCK), label: 'Sẵn hàng' },
                                                    { value: String(PRODUCT_STATUS.PRE_ORDER), label: 'Đặt hàng' }
                                                ]}
                                                value={filterStock}
                                                onChange={setFilterStock}
                                                isOpen={openDropdown === 'stock'}
                                                onToggle={() => setOpenDropdown(openDropdown === 'stock' ? null : 'stock')}
                                            />

                                            {availableBrands.length > 0 && (
                                                <SingleSelectDropdown
                                                    label="Thương hiệu"
                                                    options={[
                                                        { value: '', label: 'Tất cả hãng' },
                                                        ...availableBrands.map(b => ({ value: String(b.id), label: b.name }))
                                                    ]}
                                                    value={filterBrand}
                                                    onChange={setFilterBrand}
                                                    isOpen={openDropdown === 'brand'}
                                                    onToggle={() => setOpenDropdown(openDropdown === 'brand' ? null : 'brand')}
                                                />
                                            )}

                                            {availableSpecs.map(spec => (
                                                <SingleSelectDropdown
                                                    key={spec.groupType}
                                                    label={`Lọc ${spec.groupType}`}
                                                    options={[
                                                        { value: '', label: `Tất cả ${spec.groupType}` },
                                                        ...spec.values.map(v => ({ value: v, label: v }))
                                                    ]}
                                                    value={specFilters[spec.groupType] || ''}
                                                    onChange={v => handleSpecFilterChange(spec.groupType, v)}
                                                    isOpen={openDropdown === `spec-${spec.groupType}`}
                                                    onToggle={() => setOpenDropdown(openDropdown === `spec-${spec.groupType}` ? null : `spec-${spec.groupType}`)}
                                                />
                                            ))}

                                            <div className="flex flex-col gap-1">
                                                <label className="text-[10px] font-bold text-gray-500 uppercase">Giá tối đa (VNĐ)</label>
                                                <input
                                                    type="number"
                                                    value={filterPriceMax}
                                                    onChange={e => setFilterPriceMax(e.target.value)}
                                                    placeholder="Nhập số tiền..."
                                                    className="text-sm w-full border border-gray-200 rounded-lg px-3 py-1.5 outline-none focus:border-tnc-orange bg-white shadow-sm font-medium"
                                                    min={0}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* --- PHẦN 2: LỌC THÔNG SỐ KỸ THUẬT --- */}
                                    {availableTechSpecs.length > 0 && (
                                        <div className="pt-3 border-t border-gray-200 border-dashed">
                                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                                                {availableTechSpecs.map((specGroup, idx) => {
                                                    const selectedCount = selectedTechSpecs[specGroup.name]?.length || 0;
                                                    const isOpen = openDropdown === `tech-${specGroup.name}`;

                                                    return (
                                                        <div key={`tech-${idx}`} className="flex flex-col gap-1 relative">
                                                            <label className="text-[10px] font-bold text-gray-500 uppercase truncate" title={specGroup.name}>
                                                                {specGroup.name}
                                                            </label>
                                                            <button
                                                                onClick={() => setOpenDropdown(isOpen ? null : `tech-${specGroup.name}`)}
                                                                className={`flex items-center justify-between text-sm border rounded-lg px-2 py-1.5 outline-none shadow-sm transition-all bg-white
                                                                    ${selectedCount > 0 ? 'border-tnc-orange text-tnc-orange font-bold' : 'border-gray-200 text-gray-700 font-medium hover:border-tnc-orange'}
                                                                `}
                                                            >
                                                                <span className="truncate max-w-[120px]">
                                                                    {selectedCount === 0 ? 'Tất cả' : `${selectedCount} đã chọn`}
                                                                </span>
                                                                <ChevronDown size={14} className={`transition-transform shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
                                                            </button>

                                                            {isOpen && (
                                                                <>
                                                                    <div className="fixed inset-0 z-40" onClick={() => setOpenDropdown(null)}></div>
                                                                    <div className="absolute top-full left-0 mt-1 w-[260px] sm:w-[320px] bg-white border border-orange-200 rounded-xl shadow-xl z-[70] p-3 flex flex-col gap-2">
                                                                        <div className="max-h-48 overflow-y-auto pr-1 custom-scrollbar grid grid-cols-2 gap-x-2 gap-y-1">
                                                                            {specGroup.values.map((val, vIdx) => {
                                                                                const isChecked = selectedTechSpecs[specGroup.name]?.includes(val) || false;
                                                                                return (
                                                                                    <label key={vIdx} className="flex items-start gap-2 cursor-pointer group py-1 hover:bg-orange-50 px-1 rounded transition-colors break-words">
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={isChecked}
                                                                                            onChange={() => handleTechSpecChange(specGroup.name, val)}
                                                                                            className="mt-0.5 w-4 h-4 accent-tnc-orange cursor-pointer shrink-0"
                                                                                        />
                                                                                        <span className={`text-xs transition-colors leading-snug ${isChecked ? 'font-bold text-tnc-orange' : 'text-gray-600 group-hover:text-tnc-orange font-medium'}`}>
                                                                                            {val}
                                                                                        </span>
                                                                                    </label>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                        {selectedCount > 0 && (
                                                                            <div className="border-t border-gray-100 pt-2 flex justify-end">
                                                                                <button
                                                                                    onClick={() => {
                                                                                        const newState = { ...selectedTechSpecs };
                                                                                        delete newState[specGroup.name];
                                                                                        setSelectedTechSpecs(newState);
                                                                                    }}
                                                                                    className="text-xs text-red-500 font-bold hover:underline"
                                                                                >
                                                                                    Bỏ chọn
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Nút Xóa tất cả bộ lọc */}
                                    {activeFiltersCount > 0 && (
                                        <div className="flex justify-end pt-2 border-t border-gray-200">
                                            <button
                                                onClick={() => { 
                                                    setFilterBrand(''); 
                                                    setFilterCompat('all'); 
                                                    setFilterStock('all'); 
                                                    setFilterPriceMax(''); 
                                                    setSpecFilters({}); 
                                                    setSelectedTechSpecs({}); 
                                                }}
                                                className="text-xs font-bold text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 px-4 py-2 rounded-lg transition-colors border border-red-100 shadow-sm"
                                            >
                                                Xóa tất cả bộ lọc
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Danh sách sản phẩm & Phân trang */}
                        <div className="flex-1 overflow-y-auto bg-gray-50 flex flex-col relative z-0">
                            {/* 👉 TỐI ƯU 5: Tránh dồn layout trong Modal bằng Skeleton lưới Card */}
                            {loadingItems ? (
                                <div className="p-4 flex-1">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                                        {[...Array(8)].map((_, i) => (
                                            <div key={i} className="border border-gray-200 rounded-xl p-3 flex flex-col h-[280px] bg-white animate-pulse">
                                                <div className="w-full aspect-square bg-gray-100 rounded-lg mb-4"></div>
                                                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                                                <div className="h-4 bg-gray-200 rounded w-1/2 mb-auto"></div>
                                                <div className="h-8 bg-gray-200 rounded w-full mt-4"></div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : processedItems.length === 0 ? (
                                <div className="flex flex-col items-center justify-center flex-1 text-gray-500 gap-3 py-10">
                                    <Box size={48} className="text-gray-300" />
                                    <p className="font-bold text-lg">Không tìm thấy sản phẩm phù hợp</p>
                                    <button
                                        onClick={() => { 
                                            setSearchInput('');
                                            setSearchQuery(''); 
                                            setFilterBrand(''); 
                                            setFilterCompat('all'); 
                                            setFilterStock('all'); 
                                            setFilterPriceMax(''); 
                                            setSpecFilters({}); 
                                            setSelectedTechSpecs({}); 
                                        }}
                                        className="text-sm text-white bg-tnc-orange hover:bg-tnc-orange-hover px-4 py-2 rounded-lg font-bold shadow-sm transition-colors"
                                    >
                                        Xóa bộ lọc
                                    </button>
                                </div>
                            ) : (
                                <div className="p-4 flex-1 relative z-0">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                                        {paginatedItems.map(item => {
                                            const status = item._compatStatus;
                                            const isIncompat = status === 'incompatible';
                                            const isCompat   = status === 'compatible';

                                            return (
                                                <div
                                                    key={item.itemId}
                                                    className={`relative border rounded-xl p-3 flex flex-col transition-all duration-200 group
                                                        ${isIncompat
                                                            ? 'border-amber-300 bg-amber-50/40 hover:border-amber-400'
                                                            : isCompat
                                                                ? 'border-green-200 bg-white hover:border-green-400 hover:shadow-md hover:-translate-y-0.5'
                                                                : 'border-orange-200/60 bg-gradient-to-t from-orange-50 to-white hover:border-tnc-orange hover:shadow-md hover:-translate-y-0.5'
                                                        }`}
                                                >
                                                    <div className="absolute top-2 left-2 z-[5] flex flex-col gap-1">
                                                        {isCompat && (
                                                            <span className="flex items-center gap-1 text-[10px] font-bold bg-green-100 text-green-700 px-1.5 py-0.5 rounded border border-green-200 shadow-sm">
                                                                <CheckCircle size={10} /> Tương thích
                                                            </span>
                                                        )}
                                                        {isIncompat && (
                                                            <span className="flex items-center gap-1 text-[10px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded border border-amber-200 shadow-sm">
                                                                <AlertTriangle size={10} /> Xung đột
                                                            </span>
                                                        )}
                                                    </div>

                                                    <div className="aspect-square mb-2 flex items-center justify-center p-2 mt-4 bg-white rounded-lg relative overflow-hidden">
                                                        {/* 👉 TỐI ƯU 6: Bắt buộc Lazy load và aspect ratio hình ảnh Modal lưới */}
                                                        <img
                                                            src={getProductImage(item)}
                                                            alt={item.itemName}
                                                            loading="lazy"
                                                            decoding="async"
                                                            className={`absolute inset-0 w-full h-full object-contain transition-transform ${isIncompat ? '' : 'group-hover:scale-105'}`}
                                                        />
                                                    </div>

                                                    <Link 
                                                        to={`/san-pham/${generateSlug(item.itemName || 'san-pham')}-id${item.itemId}`} 
                                                        target="_blank"
                                                        className="font-bold text-sm text-gray-800 hover:text-tnc-orange line-clamp-2 mb-1.5 flex-1 leading-snug transition-colors"
                                                    >
                                                        {item.itemName}
                                                    </Link>

                                                    {item.compatibilityGroups?.length > 0 && (
                                                        <div className="flex flex-wrap gap-1 mb-2">
                                                            {item.compatibilityGroups.map((g, i) => (
                                                                <span key={i} className="text-[9px] bg-blue-50 text-blue-600 border border-blue-100 px-1 py-0.5 rounded font-bold">
                                                                    {g.groupType}: {g.groupValue}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}

                                                    <div className="font-black text-tnc-orange text-sm mb-2">{formatPrice(getActivePrice(item))}</div>

                                                    {isIncompat && (
                                                        <p className="text-[10px] text-amber-600 bg-amber-50 border border-amber-200 rounded p-1.5 mb-2 leading-snug">
                                                            ⚠️ Có thể không tương thích với linh kiện đã chọn
                                                        </p>
                                                    )}

                                                    <button
                                                        onClick={() => handleSelectPart(item)}
                                                        className={`w-full font-bold py-2 rounded-lg transition-colors text-sm border shadow-sm
                                                            ${isIncompat
                                                                ? 'bg-white text-amber-600 border-amber-300 hover:bg-amber-500 hover:text-white hover:border-amber-500'
                                                                : 'bg-white text-tnc-orange border-tnc-orange hover:bg-tnc-orange hover:text-white'
                                                            }`}
                                                    >
                                                        {isIncompat ? 'Vẫn chọn sản phẩm này' : 'Chọn sản phẩm này'}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* UI Phân trang (Pagination) */}
                            {!loadingItems && totalPages > 1 && (
                                <div className="border-t border-gray-200 bg-white p-3 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
                                    <div className="text-xs text-gray-500 font-bold">
                                        Hiển thị <span className="text-gray-900">{(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, processedItems.length)}</span> trên <span className="text-gray-900">{processedItems.length}</span> sản phẩm
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                            disabled={currentPage === 1}
                                            className="p-1.5 border border-gray-200 rounded-lg text-gray-500 hover:bg-orange-50 hover:text-tnc-orange hover:border-tnc-orange disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <ChevronLeft size={16} />
                                        </button>

                                        <div className="flex items-center px-2 text-sm font-bold text-gray-700">
                                            Trang {currentPage} / {totalPages}
                                        </div>

                                        <button
                                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                            disabled={currentPage === totalPages}
                                            className="p-1.5 border border-gray-200 rounded-lg text-gray-500 hover:bg-orange-50 hover:text-tnc-orange hover:border-tnc-orange disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <ChevronRight size={16} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BuildPCPage;